/**
 * Event Form Modal
 *
 * Modal for creating/editing calendar events.
 * Opens with pre-populated values from calendar slot click.
 */

import React from 'react';
import { Modal, message } from 'antd';
import dayjs from 'dayjs';
import { UniversalRULForm } from '@/components/features/rul/UniversalRULForm';
import { getTenantFromDomain } from '@/utils/tenant';

interface EventFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (data: any) => void;
  // Pre-populated values
  startTime?: Date;
  departureAirportId?: string;
}

/**
 * Map tenant code to form endpoint
 */
const TENANT_FORM_ENDPOINTS: Record<string, string> = {
  GOLDENWINGS: '/calendars/forms/flight-scheduler/event',
  BELLVILLEDENTAL: '/formsexamples/forms/event/dentist-appointment',
};

const DEFAULT_FORM_ENDPOINT = '/calendars/forms/flight-scheduler/event';

export const EventFormModal: React.FC<EventFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  startTime,
  departureAirportId,
}) => {
  // Build form URL with query params for pre-population
  const formUrl = React.useMemo(() => {
    const tenant = getTenantFromDomain();
    let baseUrl = TENANT_FORM_ENDPOINTS[tenant || ''] || DEFAULT_FORM_ENDPOINT;

    // Add query parameters for pre-population
    const params = new URLSearchParams();

    if (startTime) {
      // Format as ISO datetime for backend
      params.append(
        'start_time',
        dayjs(startTime).format('YYYY-MM-DDTHH:mm:ss')
      );
    }

    if (departureAirportId) {
      params.append('departure_airport', departureAirportId);
    }

    const queryString = params.toString();
    if (queryString) {
      baseUrl += (baseUrl.includes('?') ? '&' : '?') + queryString;
    }

    return baseUrl;
  }, [startTime, departureAirportId]);

  const handleSuccess = (data: any) => {
    message.success('Event kreiran uspešno!');
    onSuccess?.(data);
    onClose();
  };

  const handleError = (errors: Record<string, string>) => {
    message.error('Molimo ispravite greške u formi');
    console.error('Validation errors:', errors);
  };

  return (
    <Modal
      title="Novi Event"
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ top: '5vh', maxWidth: '1400px' }}
      styles={{
        body: {
          height: 'calc(90vh - 110px)',
          overflow: 'auto',
          padding: '24px',
        },
      }}
      destroyOnClose
      className="event-form-modal"
    >
      {open && (
        <UniversalRULForm
          formUrl={formUrl}
          onSuccess={handleSuccess}
          onError={handleError}
          className="event-form"
          showSubmitButton={true}
        />
      )}
    </Modal>
  );
};
